import { isNonNullable } from "../../utils";
import { BaseStore } from "./BaseStore";
import { type Draft, produce } from "immer";
import { type PrimitiveAtom, atom, type Getter } from "jotai";

export type TreeItemResult<T> = T & {
  childrenItems?: Array<TreeItemResult<T>>;
};

export interface TreeStoreOptions<T> {
  keyProp: keyof T;
  childrenProp: keyof T;
  rootKey?: string;
  treeItems?: Record<string, T>;
  treeItemsAtom?: PrimitiveAtom<Record<string, T>>;
}
const DEFAULT_ROOT_KEY = "___root___";
export class TreeStore<T extends object> extends BaseStore {
  get keyProp() {
    return this.options.keyProp;
  }
  get childrenProp() {
    return this.options.childrenProp;
  }

  rootKey: PrimitiveAtom<string>;
  treeItems: PrimitiveAtom<Record<string, T>>;

  parentKeyMapping = atom<Record<string, string>>((get) => {
    const treeItems = get(this.treeItems);

    const parentKeyMapping: Record<string, string> = {};
    Object.keys(treeItems).forEach((key) => {
      const item = treeItems[key];

      const childrenKeys = item[this.childrenProp] as string[];
      if (!childrenKeys) {
        return;
      }
      childrenKeys.forEach((childKey) => {
        parentKeyMapping[childKey] = key;
      });
    });

    return parentKeyMapping;
  });

  constructor(private readonly options: TreeStoreOptions<T>) {
    super();

    this.rootKey = atom(options.rootKey ?? DEFAULT_ROOT_KEY);
    this.treeItems = options.treeItemsAtom ?? atom(options.treeItems ?? {});
  }

  setTreeItems(treeItems: Record<string, T>) {
    this.set(this.treeItems, treeItems);
  }

  getItem(
    key?: string,
    options: {
      withChildren?: boolean;
      get?: Getter;
    } = {}
  ): TreeItemResult<T> | undefined {
    const { get = this.get } = options;
    const itemAtom = atom(
      (get) => get(this.treeItems)[key ?? get(this.rootKey)]
    );

    const item = get(itemAtom);
    if (!item) {
      return;
    }

    if (options.withChildren) {
      const newItem: TreeItemResult<T> = {
        ...item,
        childrenItems: this.getChildrenItems(key, options),
      };
      return newItem;
    }

    return item;
  }

  getChildrenKeys(
    key?: string,
    options: {
      withChildren?: boolean;
      get?: Getter;
    } = {}
  ): string[] | undefined {
    return this.getItem(key, options)?.[this.childrenProp] as string[];
  }

  getChildrenItems(
    key?: string,
    options: {
      withChildren?: boolean;
      get?: Getter;
    } = {}
  ): Array<TreeItemResult<T>> | undefined {
    const { get = this.get } = options;

    return this.getChildrenKeys(key, { get })
      ?.map((childKey) => this.getItem(childKey, options))
      .filter(isNonNullable);
  }

  getParentKey(key: string, get = this.get) {
    return get(this.parentKeyMapping)[key];
  }

  getParentItem(
    key: string,
    options: {
      withChildren?: boolean;
      get?: Getter;
    } = {}
  ) {
    const { get = this.get } = options;

    return this.getItem(this.getParentKey(key, get), options);
  }

  setItem(key: string, data: T) {
    this.set(this.treeItems, (prev) =>
      produce(prev, (draft) => {
        draft[key] = {
          // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/no-misused-spread
          ...draft[key],
          ...data,
        };
      })
    );
  }

  setChildren(key: string | undefined, children: T[] | undefined) {
    this.set(this.treeItems, (prev) =>
      produce(prev, (draft) => {
        const updatedKey = key ?? this.get(this.rootKey);

        draft[updatedKey] ||= {
          key: updatedKey,
        } as Draft<T>;
        if (!children) {
          this.setItemChildrenKeys(draft[updatedKey] as T, undefined);
          return;
        }

        const keys: string[] = [];
        children?.forEach((child) => {
          const key = child[this.keyProp] as string;
          keys.push(key);

          draft[key] = {
            // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/no-misused-spread
            ...draft[key],
            ...child,
          };
        });

        this.setItemChildrenKeys(draft[updatedKey] as T, keys);
      })
    );
  }

  private setItemChildrenKeys(item: T, childrenKeys: string[] | undefined) {
    (item as any)[this.childrenProp] = childrenKeys;
  }
}
